import React, { useEffect, useRef, useState } from "react";
import { ParsedFilter } from "@ui-components/Searchbar/GenericSearchbar";

export type LogicOp = "AND" | "OR";

export interface FilterNode {
  filter: ParsedFilter;
  op?: LogicOp;
  not?: boolean;
}

interface Props {
  nodes: FilterNode[];
  onChange: (next: FilterNode[]) => void;
  suggestedKeys: string[];
  placeholder?: string;
}

interface ParseResult {
  filter: ParsedFilter;
  not: boolean;
}

function parseInput(raw: string): ParseResult | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  // key (no !=:) [optional !] (= or :) value
  const m = trimmed.match(/^\s*([^!=:]+?)\s*(!?)\s*([:=])\s*(.+?)\s*$/);
  if (m) {
    return {
      filter: { key: m[1], value: m[4], op: m[3] as ":" | "=" },
      not: m[2] === "!",
    };
  }
  return { filter: { key: "", value: trimmed }, not: false };
}

function filterToString(node: FilterNode): string {
  const { filter, not } = node;
  if (!filter.key) return filter.value;
  const op = `${not ? "!" : ""}${filter.op || "="}`;
  return `${filter.key} ${op} ${filter.value}`;
}

const Chip: React.FC<{
  filter: ParsedFilter;
  not: boolean;
  onRemove: () => void;
  onEdit: () => void;
  onToggleNot: () => void;
}> = ({ filter, not, onRemove, onEdit, onToggleNot }) => {
  const opSymbol = (filter.op || ":") === "=" ? (not ? "≠" : "=") : (not ? "!:" : ":");
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit();
        }
      }}
      title="Click to edit"
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs border cursor-pointer transition-colors ${
        not
          ? "bg-semantic-bg-danger-secondary text-semantic-text-danger border-semantic-border-danger-primary hover:border-semantic-text-danger"
          : "bg-semantic-bg-brand-secondary text-semantic-text-primary border-semantic-border-secondary hover:border-semantic-border-brand-secondary"
      }`}
    >
      {filter.key && (
        <>
          <span className="font-semibold">{filter.key}</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleNot();
            }}
            title={not ? "Click to include (remove NOT)" : "Click to exclude (NOT)"}
            className="font-mono opacity-70 hover:opacity-100 px-0.5"
          >
            {opSymbol}
          </button>
        </>
      )}
      <span className="font-mono">{filter.value}</span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="ml-0.5 w-4 h-4 inline-flex items-center justify-center text-semantic-text-secondary hover:text-semantic-text-danger rounded"
        aria-label="Remove filter"
      >
        ×
      </button>
    </span>
  );
};

const EditChip: React.FC<{
  initial: string;
  onCommit: (raw: string) => void;
  onCancel: () => void;
}> = ({ initial, onCommit, onCancel }) => {
  const [value, setValue] = useState(initial);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  return (
    <input
      ref={ref}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          onCommit(value);
        } else if (e.key === "Escape") {
          e.preventDefault();
          onCancel();
        }
      }}
      onBlur={() => onCommit(value)}
      style={{ width: `${Math.max(value.length + 2, 8)}ch` }}
      className="bg-semantic-bg-primary text-semantic-text-primary text-xs font-mono border border-semantic-border-brand-secondary rounded-md px-2 py-1 outline-none"
    />
  );
};

const OpToggle: React.FC<{ op: LogicOp; onToggle: () => void }> = ({
  op,
  onToggle,
}) => (
  <button
    type="button"
    onClick={onToggle}
    title="Click to toggle AND / OR"
    className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase rounded transition-colors border ${
      op === "AND"
        ? "bg-semantic-bg-brand-secondary text-semantic-text-brand border-semantic-border-brand-secondary"
        : "bg-semantic-bg-warning-secondary text-semantic-text-warning border-semantic-border-primary"
    }`}
  >
    {op}
  </button>
);

const LogsFilterBuilder: React.FC<Props> = ({
  nodes,
  onChange,
  suggestedKeys,
  placeholder = "Filter by attribute:value (e.g., Level = ERROR)",
}) => {
  const [input, setInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [pendingOp, setPendingOp] = useState<LogicOp | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const leftSide = input.split(/[=:]/, 1)[0].trim().toLowerCase();
  const hasOpInInput = /[=:]/.test(input);
  const matchingKeys = hasOpInInput
    ? []
    : suggestedKeys.filter((k) =>
        leftSide === "" ? true : k.toLowerCase().includes(leftSide)
      );

  const addFilter = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;

    // Bare "AND" / "OR" — remember it as the connector for the NEXT filter
    // typed. Only meaningful when there's already a chip to chain with.
    const bare = trimmed.match(/^(AND|OR)$/i);
    if (bare) {
      if (nodes.length > 0) {
        setPendingOp(bare[1].toUpperCase() as LogicOp);
      }
      setInput("");
      return;
    }

    // Connector prefix in the same input ("OR foo" / "AND foo") wins over a
    // pendingOp; falls back to pendingOp; finally defaults to AND.
    let joinOp: LogicOp = pendingOp ?? "AND";
    let cleaned = trimmed;
    const connector = trimmed.match(/^(AND|OR)\s+(.+)$/i);
    if (connector) {
      joinOp = connector[1].toUpperCase() === "OR" ? "OR" : "AND";
      cleaned = connector[2];
    }

    const parsed = parseInput(cleaned);
    if (!parsed) return;
    const nextNode: FilterNode = {
      filter: parsed.filter,
      op: nodes.length > 0 ? joinOp : undefined,
      ...(parsed.not ? { not: true } : {}),
    };
    onChange([...nodes, nextNode]);
    setInput("");
    setPendingOp(null);
  };

  const toggleNot = (idx: number) => {
    const next = [...nodes];
    next[idx] = { ...next[idx], not: !next[idx].not };
    onChange(next);
  };

  const removeAt = (idx: number) => {
    const next = nodes.filter((_, i) => i !== idx);
    if (next.length > 0) next[0] = { ...next[0], op: undefined };
    if (next.length === 0) setPendingOp(null);
    onChange(next);
  };

  const toggleOp = (idx: number) => {
    const next = [...nodes];
    next[idx] = {
      ...next[idx],
      op: next[idx].op === "AND" ? "OR" : "AND",
    };
    onChange(next);
  };

  const pickSuggestion = (key: string) => {
    setInput(`${key} = `);
    inputRef.current?.focus();
  };

  const commitEdit = (idx: number, raw: string) => {
    const parsed = parseInput(raw);
    if (!parsed) {
      removeAt(idx);
    } else {
      const next = [...nodes];
      next[idx] = {
        ...next[idx],
        filter: parsed.filter,
        not: parsed.not || undefined,
      };
      onChange(next);
    }
    setEditingIdx(null);
  };

  return (
    <div className="relative">
      <div
        className="flex flex-wrap items-center gap-2 min-h-[40px] px-3 py-2 bg-semantic-bg-primary border border-semantic-border-secondary rounded-md focus-within:border-semantic-border-brand-secondary transition-colors cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        {nodes.map((node, i) => (
          <React.Fragment key={i}>
            {i > 0 && node.op && (
              <OpToggle op={node.op} onToggle={() => toggleOp(i)} />
            )}
            {editingIdx === i ? (
              <EditChip
                initial={filterToString(node)}
                onCommit={(raw) => commitEdit(i, raw)}
                onCancel={() => setEditingIdx(null)}
              />
            ) : (
              <Chip
                filter={node.filter}
                not={Boolean(node.not)}
                onRemove={() => removeAt(i)}
                onEdit={() => setEditingIdx(i)}
                onToggleNot={() => toggleNot(i)}
              />
            )}
          </React.Fragment>
        ))}
        {pendingOp && (
          <span
            className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase rounded border ${
              pendingOp === "AND"
                ? "bg-semantic-bg-brand-secondary text-semantic-text-brand border-semantic-border-brand-secondary"
                : "bg-semantic-bg-warning-secondary text-semantic-text-warning border-semantic-border-primary"
            }`}
            title="Connector applied to the next filter you add"
          >
            {pendingOp}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setPendingOp(null);
              }}
              className="ml-1 w-3 h-3 inline-flex items-center justify-center text-semantic-text-secondary hover:text-semantic-text-danger"
              aria-label="Cancel pending connector"
            >
              ×
            </button>
          </span>
        )}
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setShowSuggestions(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addFilter(input);
            } else if (
              e.key === "Backspace" &&
              input === "" &&
              (pendingOp || nodes.length > 0)
            ) {
              e.preventDefault();
              if (pendingOp) {
                setPendingOp(null);
              } else {
                removeAt(nodes.length - 1);
              }
            } else if (e.key === "Escape") {
              setShowSuggestions(false);
            }
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          placeholder={nodes.length === 0 ? placeholder : "Add filter…"}
          className="flex-1 min-w-[180px] bg-transparent outline-none text-sm text-semantic-text-primary placeholder:text-semantic-text-secondary"
        />
      </div>

      {showSuggestions && matchingKeys.length > 0 && (
        <div className="absolute left-0 top-full mt-1 z-20 bg-semantic-bg-primary border border-semantic-border-secondary rounded-md shadow-md min-w-[240px] overflow-hidden">
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-semantic-text-secondary border-b border-semantic-border-secondary bg-semantic-bg-secondary">
            Suggested keys
          </div>
          {matchingKeys.map((k) => (
            <button
              key={k}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                pickSuggestion(k);
              }}
              className="block w-full text-left px-3 py-1.5 text-sm text-semantic-text-primary hover:bg-semantic-state-active font-mono"
            >
              <span className="font-semibold">{k}</span>
              <span className="opacity-60"> = </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LogsFilterBuilder;
